import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, X, Clock, CornerDownLeft } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { getIcon } from "@/lib/iconMap";
import { cn } from "@/lib/utils";
import {
  useDebounced,
  useRecentSearches,
  useSearchQuery,
  type SearchResult,
} from "./useGlobalSearch";

interface ModuleMeta {
  key: string;
  label: string;
  icon: string;
  listPath: string;
}

// Order mirrors the backend MODULE_ORDER (business → operational → finance →
// resources). Icons reuse the existing iconMap so rows match the sidebar.
const MODULES: ModuleMeta[] = [
  { key: "company", label: "Companies", icon: "Building2", listPath: "/companies" },
  { key: "product", label: "Products", icon: "Package", listPath: "/products" },
  { key: "project", label: "Projects", icon: "FolderKanban", listPath: "/projects" },
  { key: "contact", label: "Contacts", icon: "Users", listPath: "/contacts" },
  { key: "meeting", label: "Meetings", icon: "CalendarDays", listPath: "/meetings" },
  { key: "task", label: "Tasks", icon: "CheckSquare", listPath: "/tasks" },
  { key: "action", label: "Actions", icon: "Zap", listPath: "/actions" },
  { key: "note", label: "Notes", icon: "FileText", listPath: "/notes" },
  { key: "idea", label: "Ideas", icon: "Lightbulb", listPath: "/ideas" },
  { key: "decision", label: "Decisions", icon: "Scale", listPath: "/decisions" },
  { key: "milestone", label: "Milestones", icon: "Flag", listPath: "/milestones" },
  { key: "budget", label: "Budgets", icon: "Wallet", listPath: "/budgets" },
  { key: "expense", label: "Expenses", icon: "Receipt", listPath: "/expenses" },
  { key: "estimation", label: "Cost Estimations", icon: "Calculator", listPath: "/estimations" },
  { key: "proposal", label: "Price Proposals", icon: "ClipboardList", listPath: "/proposals" },
  { key: "asset", label: "Assets", icon: "Layers", listPath: "/assets" },
  { key: "vault", label: "Access Vault", icon: "KeyRound", listPath: "/access-vault" },
  { key: "media", label: "Media Files", icon: "Paperclip", listPath: "/media-vault" },
  { key: "issue", label: "QA Issues", icon: "Bug", listPath: "/qa/issues" },
  { key: "partner", label: "Partners", icon: "Handshake", listPath: "/partners" },
  { key: "finance_account", label: "Accounts", icon: "Landmark", listPath: "/finance/accounts" },
  { key: "finance_income", label: "Income", icon: "TrendingUp", listPath: "/finance/income" },
  { key: "finance_contribution", label: "Contributions", icon: "PiggyBank", listPath: "/finance/contributions" },
];

interface GlobalSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GlobalSearchModal({
  open,
  onOpenChange,
}: GlobalSearchModalProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const debouncedQuery = useDebounced(query, 300);
  const { recent, add: addRecent } = useRecentSearches();
  const { data, isFetching, isError, refetch } = useSearchQuery(
    debouncedQuery,
    selectedKeys,
  );

  const hasQuery = debouncedQuery.trim().length >= 2;

  // Reset transient UI state whenever the modal opens.
  useEffect(() => {
    if (open) {
      setActiveIndex(0);
      // Autofocus — rAF lets Radix finish mounting the content first.
      const id = window.requestAnimationFrame(() => inputRef.current?.focus());
      return () => window.cancelAnimationFrame(id);
    }
    setQuery("");
    setSelectedKeys([]);
  }, [open]);

  // Record a recent search once a query with results comes back.
  useEffect(() => {
    if (hasQuery && data && data.totalResults > 0) {
      addRecent(debouncedQuery.trim());
    }
  }, [hasQuery, data, debouncedQuery, addRecent]);

  // Flatten visible results in module order for keyboard navigation.
  const flat = useMemo(() => {
    if (!data) return [] as { module: ModuleMeta; result: SearchResult }[];
    const out: { module: ModuleMeta; result: SearchResult }[] = [];
    for (const m of MODULES) {
      const rows = data.results[m.key];
      if (rows && rows.length > 0) {
        for (const r of rows) out.push({ module: m, result: r });
      }
    }
    return out;
  }, [data]);

  useEffect(() => {
    setActiveIndex(0);
  }, [debouncedQuery, selectedKeys]);

  const go = useCallback(
    (url: string) => {
      onOpenChange(false);
      navigate(url);
    },
    [navigate, onOpenChange],
  );

  const toggleModule = useCallback((key: string) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (flat.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const sel = flat[activeIndex];
        if (sel) go(sel.result.url);
      }
    },
    [flat, activeIndex, go],
  );

  const totalResults = data?.totalResults ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl gap-0 overflow-hidden p-0"
        onKeyDown={onKeyDown}
      >
        <DialogTitle className="sr-only">Global search</DialogTitle>
        <DialogDescription className="sr-only">
          Search across all PhazeX Hub modules
        </DialogDescription>

        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search across PhazeX Hub…"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoComplete="off"
            spellCheck={false}
          />
          {isFetching && (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
          )}
          {query && !isFetching && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                inputRef.current?.focus();
              }}
              className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Module filter chips */}
        <div className="flex flex-wrap gap-1.5 border-b border-border px-4 py-2.5">
          <button
            type="button"
            onClick={() => setSelectedKeys([])}
            className={cn(
              "rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
              selectedKeys.length === 0
                ? "bg-primary/10 text-primary"
                : "border border-border text-muted-foreground hover:bg-muted",
            )}
          >
            All
          </button>
          {MODULES.map((m) => {
            const selected = selectedKeys.includes(m.key);
            return (
              <button
                key={m.key}
                type="button"
                onClick={() => toggleModule(m.key)}
                className={cn(
                  "rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
                  selected
                    ? "bg-primary/10 text-primary"
                    : "border border-border text-muted-foreground hover:bg-muted",
                )}
              >
                {m.label}
              </button>
            );
          })}
        </div>

        {/* Results / states */}
        <div className="max-h-[55vh] min-h-[180px] overflow-y-auto px-2 py-2">
          {!hasQuery && (
            <EmptyInitial recent={recent} onPick={(q) => setQuery(q)} />
          )}

          {hasQuery && isError && (
            <div className="flex flex-col items-center gap-3 py-12 text-sm text-muted-foreground">
              <p>Search failed. Please try again.</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
              >
                Retry
              </button>
            </div>
          )}

          {hasQuery && !isError && isFetching && !data && (
            <div className="space-y-2 px-2 py-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 px-2 py-2"
                >
                  <div className="h-7 w-7 animate-pulse rounded bg-muted" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
                    <div className="h-2.5 w-1/2 animate-pulse rounded bg-muted" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {hasQuery && !isError && data && totalResults === 0 && !isFetching && (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No results for “{debouncedQuery.trim()}”. Try different keywords or
              check spelling.
            </div>
          )}

          {hasQuery && !isError && data && totalResults > 0 && (
            <SearchResultsList
              data={data}
              flat={flat}
              activeIndex={activeIndex}
              onHover={setActiveIndex}
              onSelect={go}
              onShowAll={(listPath) => go(listPath)}
            />
          )}
        </div>

        {/* Footer hint */}
        <div className="flex items-center justify-between border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <CornerDownLeft className="h-3 w-3" /> to open · ↑↓ to navigate · Esc
            to close
          </span>
          <span>Ctrl/Cmd + K · /</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EmptyInitial({
  recent,
  onPick,
}: {
  recent: string[];
  onPick: (q: string) => void;
}) {
  return (
    <div className="px-2 py-2">
      <p className="px-2 py-6 text-center text-sm text-muted-foreground">
        Type to search across PhazeX Hub. Press{" "}
        <kbd className="rounded border border-border bg-muted px-1 text-[10px]">
          Ctrl/Cmd + K
        </kbd>{" "}
        from anywhere.
      </p>
      {recent.length > 0 && (
        <div className="mt-1">
          <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Recent searches
          </p>
          {recent.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => onPick(q)}
              className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm text-foreground hover:bg-muted"
            >
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SearchResultsList({
  data,
  flat,
  activeIndex,
  onHover,
  onSelect,
  onShowAll,
}: {
  data: { results: Record<string, SearchResult[]>; counts: Record<string, number> };
  flat: { module: ModuleMeta; result: SearchResult }[];
  activeIndex: number;
  onHover: (i: number) => void;
  onSelect: (url: string) => void;
  onShowAll: (listPath: string) => void;
}) {
  // Module order is enforced by iterating MODULES; flatIndex aligns with `flat`.
  let flatIndex = -1;
  return (
    <div>
      {MODULES.map((m) => {
        const rows = data.results[m.key];
        if (!rows || rows.length === 0) return null;
        const Icon = getIcon(m.icon);
        const total = data.counts[m.key] ?? rows.length;
        return (
          <div key={m.key} className="mb-1">
            <div className="flex items-center gap-2 px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <Icon className="h-3.5 w-3.5" />
              {m.label} ({total})
            </div>
            {rows.map((r) => {
              flatIndex += 1;
              const idx = flatIndex;
              const active = idx === activeIndex && flat[idx]?.result.id === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  onMouseEnter={() => onHover(idx)}
                  onClick={() => onSelect(r.url)}
                  className={cn(
                    "flex w-full items-start gap-3 rounded-md px-3 py-2 text-left transition-colors",
                    active ? "bg-primary/10" : "hover:bg-muted",
                  )}
                >
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline gap-1.5">
                      <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                        {r.code}
                      </span>
                      <span className="truncate text-sm font-medium text-foreground">
                        {r.title}
                      </span>
                    </span>
                    {r.subtitle && (
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                        {r.subtitle}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
            {total > rows.length && (
              <button
                type="button"
                onClick={() => onShowAll(m.listPath)}
                className="block w-full px-3 py-1.5 text-left text-xs font-medium text-primary hover:underline"
              >
                Show all {total} results in {m.label} →
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
